import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { pickDetail } from '../lib/data'
import { useAuth } from '../hooks/useAuth'

const TIER_STYLE = {
  'Critical': { bg: '#FCEBEB', color: '#A32D2D', dot: '🔴' },
  'High':     { bg: '#FAEEDA', color: '#854F0B', dot: '🟡' },
  'Medium':   { bg: '#EAF3DE', color: '#3B6D11', dot: '🟢' },
  'Low':      { bg: '#F1EFE8', color: '#5F5E5A', dot: '⚪' },
}

export default function NewAudit() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const [sites, setSites] = useState([])
  const [search, setSearch] = useState('')
  const [filterTier, setFilterTier] = useState('All')
  const [loading, setLoading] = useState(true)
  const [creating, setCreating] = useState(false)

  useEffect(() => {
    supabase
      .from('sites')
      .select('*, site_details(priority_tier, priority_score, lpr_issue, gate_issue, district, rank)')
      .eq('active', true)
      .order('name')
      .then(({ data }) => {
        // Sort by priority rank if available
        const sorted = (data || []).sort((a, b) => {
          const ra = pickDetail(a.site_details)?.rank ?? 9999
          const rb = pickDetail(b.site_details)?.rank ?? 9999
          return ra - rb
        })
        setSites(sorted)
        setLoading(false)
      })
  }, [])

  async function startAudit(siteId) {
    setCreating(true)
    const { data, error } = await supabase
      .from('audits')
      .insert({ site_id: siteId, auditor_id: user.id })
      .select()
      .single()
    if (!error) navigate(`/audits/${data.id}`)
    else { alert('Error creating audit: ' + error.message); setCreating(false) }
  }

  const tiers = ['All', 'Critical', 'High', 'Medium', 'Low']

  const filtered = sites.filter(s => {
    const tier = pickDetail(s.site_details)?.priority_tier || ''
    const matchSearch = s.name.toLowerCase().includes(search.toLowerCase()) ||
      (s.city || '').toLowerCase().includes(search.toLowerCase()) ||
      (pickDetail(s.site_details)?.district || '').toLowerCase().includes(search.toLowerCase())
    const matchTier = filterTier === 'All' || tier === filterTier
    return matchSearch && matchTier
  })

  if (loading) return <div className="spinner" />

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Select a site</div>
          <div className="page-subtitle">Sorted by priority — {sites.length} total sites</div>
        </div>
        <button className="btn btn-sm" onClick={() => navigate('/audits')}>Cancel</button>
      </div>

      <input
        className="form-input"
        placeholder="Search by name, city, or district..."
        value={search}
        onChange={e => setSearch(e.target.value)}
        style={{ marginBottom: 10 }}
        autoFocus
      />

      {/* Tier filter pills */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 16, flexWrap: 'wrap' }}>
        {tiers.map(t => {
          const style = TIER_STYLE[t]
          const active = filterTier === t
          return (
            <button
              key={t}
              onClick={() => setFilterTier(t)}
              style={{
                padding: '4px 12px',
                borderRadius: 20,
                border: `1px solid ${active && style ? style.color : 'var(--border)'}`,
                background: active && style ? style.bg : 'var(--bg)',
                color: active && style ? style.color : 'var(--text-muted)',
                fontSize: 13,
                fontWeight: active ? 600 : 400,
                cursor: 'pointer',
              }}
            >
              {style?.dot} {t}
            </button>
          )
        })}
      </div>

      {creating && (
        <div style={{ textAlign: 'center', padding: 20, color: 'var(--text-muted)', fontSize: 14 }}>
          Creating audit…
        </div>
      )}

      {filtered.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">🔍</div>
          <h3>No sites found</h3>
          <p>Try a different search or filter.</p>
        </div>
      ) : (
        filtered.map(site => {
          const det = pickDetail(site.site_details)
          const tier = det?.priority_tier
          const ts = TIER_STYLE[tier]
          return (
            <div
              key={site.id}
              className="site-item"
              onClick={() => !creating && startAudit(site.id)}
              style={{ opacity: creating ? 0.5 : 1 }}
            >
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <div className="site-name">{site.name}</div>
                  {tier && (
                    <span style={{
                      fontSize: 11, padding: '2px 7px', borderRadius: 10,
                      background: ts?.bg, color: ts?.color, fontWeight: 500,
                    }}>
                      {ts?.dot} {tier}
                    </span>
                  )}
                </div>
                <div className="site-meta">
                  {[site.city, site.state].filter(Boolean).join(', ')}
                  {det?.district ? ` · ${det.district}` : ''}
                  {det?.lpr_issue ? ' · ⚠️ LPR issue' : ''}
                  {det?.gate_issue ? ' · ⚠️ Gate issue' : ''}
                </div>
              </div>
              <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"
                style={{ width: 18, height: 18, color: 'var(--text-muted)', flexShrink: 0 }}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
              </svg>
            </div>
          )
        })
      )}
    </div>
  )
}
